import { z } from 'zod/v4-mini';

import { GeneralBackendResponseSchema } from '$/services/mapper/authentication';
import { type Material } from '$/services/usecases/materials/mapper/materials';
import { unsecureHashCode, unsecureSeededRandom } from '$/utils/numberUtils';

export const InspirationCornerLineSchema = z.tuple([
  z.tuple([z.number(), z.number()]),
  z.tuple([z.number(), z.number()]),
]);

export const InspirationSchema = z.pipe(
  z.object({
    id: z.string(),
    name: z.string(),
    status: z.optional(z.enum(['pending', 'processing', 'ready', 'error'])),
    category: z.string(),
    viewCount: z.number(),
    thumbnail: z.string(),
    viewThumbnails: z.array(z.string()),
    mainWallComponentId: z.optional(z.nullable(z.string())),
  }),
  z.transform((data) => ({
    ...data,
    mainWallComponentId: data.mainWallComponentId ?? null,
    clicks: Math.floor(
      unsecureSeededRandom(unsecureHashCode(data.id)) * 100000,
    ),
  })),
);

export const DetailedInspirationSchema = z.pipe(
  z.intersection(
    InspirationSchema,
    z.object({
      components: z.array(
        z.object({
          id: z.string(),
          key: z.string(),
          category: z.enum(['floor', 'wall', 'ceiling', 'outside', 'facade']),
          name: z.string(),
          initialMaterialId: z.nullable(z.string()),
          isVisible: z.optional(z.boolean()),
        }),
      ),
      views: z.array(
        z.object({
          id: z.number(),
          thumbnail: z.string(),
          components: z.array(
            z.object({
              id: z.string(),
              corners: z.array(z.tuple([z.number(), z.number()])),
              cornerLines: z.optional(z.array(InspirationCornerLineSchema)),
              objectWidth: z.number(),
              objectHeight: z.number(),
            }),
          ),
          layers: z.optional(z.array(z.string())),
        }),
      ),
      preview: z.string(),
      exr: z.optional(
        z.array(
          z.object({
            filename: z.string(),
            status: z.enum(['pending', 'processing', 'ready', 'error']),
            message: z.optional(z.string()),
          }),
        ),
      ),
      render: z.object({
        layers: z.record(z.string(), z.string()),
        borders: z.record(z.string(), z.string()),
        masks: z.record(z.string(), z.string()),
      }),
    }),
  ),
  z.transform((data) => ({
    ...data,
    components: Object.fromEntries(data.components.map((o) => [o.id, o])),
  })),
);

export type Inspiration = z.infer<typeof InspirationSchema>;
export type DetailedInspiration = z.infer<typeof DetailedInspirationSchema> & {
  materials: Record<string, Material>;
};
export type InspirationComponent = DetailedInspiration['components'][0];
export type InspirationComponentCategory = InspirationComponent['category'];

export const GetAllInspirationsSchema = z.object({
  ...GeneralBackendResponseSchema,
  payload: z.array(InspirationSchema),
});

export const GetInspirationByIdSchema = z.object({
  ...GeneralBackendResponseSchema,
  payload: DetailedInspirationSchema,
});

export const UpdateInspirationSchema = z.object({
  ...GeneralBackendResponseSchema,
  payload: DetailedInspirationSchema,
});
